import React from 'react';
import Link from 'next/link';
import {CtaContent} from './types';
import {colors, fontWeight} from '@/styles/tokens';

// ─── CtaCard ─────────────────────────────────────────────────────────────────
// A self-contained, reusable CTA card (owns its hover button and inline styles).
// Extracted because it's a coherent UI component in its own right; the parser's
// other one-off markup stays inlined with its option builder in `./parser-options`.

// Inline styles for the CTA card. Kept inline (not just SCSS classes) so the
// design renders correctly even when the global stylesheet is stale/cached.
const ctaStyles: Record<string, React.CSSProperties> = {
    card: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 18,
        background: 'linear-gradient(135deg, #0a1628 0%, #0f2554 60%, #1a3a7a 100%)',
        borderRadius: 16,
        padding: '28px 32px',
        margin: '36px 0'
    },
    left: {display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minWidth: 220},
    tag: {display: 'inline-flex', alignItems: 'center', gap: 8, width: 'fit-content'},
    tagIcon: {
        width: 36,
        height: 36,
        background: colors.primary.navy,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    },
    tagIconSvg: {fontSize: 18, color: '#fff'},
    tagText: {
        fontFamily: "'Headers', sans-serif",
        fontSize: 13,
        fontWeight: fontWeight.bold,
        letterSpacing: '0.1em',
        color: 'rgba(255,255,255,0.85)',
        textTransform: 'uppercase'
    },
    title: {
        fontFamily: "'Headers', sans-serif",
        fontSize: 26,
        fontWeight: fontWeight.bold,
        color: '#fff',
        lineHeight: 1.25,
        margin: 0
    },
    desc: {
        fontFamily: "'RegularText', sans-serif",
        fontSize: 16,
        color: 'rgba(255,255,255,0.7)',
        lineHeight: 1.6,
        margin: 0,
        letterSpacing: '0.3px'
    },
    right: {display: 'flex', justifyContent: 'flex-start'},
    btn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 12,
        background: colors.background.default,
        color: colors.primary.main,
        fontFamily: "'Headers', sans-serif",
        fontSize: 16,
        fontWeight: fontWeight.semibold,
        textTransform: 'none',
        textDecoration: 'none',
        padding: '10px 30px',
        border: '1px solid #fff',
        whiteSpace: 'nowrap'
    }
};

// CTA button with JS-driven hover so the effect works even when the global
// stylesheet is stale/cached (see project memory on the next-pwa CSS cache).
function CtaButton({href, label}: {href: string; label: string}) {
    const [hover, setHover] = React.useState(false);
    const hoverStyle: React.CSSProperties = hover ? {background: 'transparent', color: '#fff', borderColor: '#fff'} : {};
    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="bdn-cta-btn"
            style={{...ctaStyles.btn, ...hoverStyle}}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {label}&nbsp;&#8594;
        </Link>
    );
}

// Renders a single CTA card with our bdn-cta-card design.
export function CtaCard({cta}: {cta: CtaContent}) {
    return (
        <div className="bdn-cta-card" style={ctaStyles.card}>
            <div className="bdn-cta-left" style={ctaStyles.left}>
                <div className="bdn-cta-title" style={ctaStyles.title}>
                    {cta.title}
                </div>
                {cta.description && (
                    <div className="bdn-cta-desc" style={ctaStyles.desc}>
                        {cta.description}
                    </div>
                )}
            </div>
            <div className="bdn-cta-right" style={ctaStyles.right}>
                <CtaButton href={cta.btnHref} label={cta.btnLabel} />
            </div>
        </div>
    );
}
